import type { Parts } from "@codingame/monaco-vscode-workbench-service-override";
import { parseTabURI } from "components/Layout/Panels/VSCode/editorSync";
import { useLayoutButtons } from "components/Layout/Panels/VSCode/useLayoutButtons";
import { useEditor } from "features/editor/Feature";
import type { TabParams } from "features/types/tabs";
import { usePitcher } from "features/utils/usePitcher";
import { useEditorChannel } from "hooks/pitcher/useEditorChannel";
import { useEffect, useState } from "react";
import * as vscode from "vscode";
import { DisposableStore } from "vscode/monaco";
import type { IEditorOptions } from "vscode/services";
import {
  IEditorService,
  StandaloneServices,
  IWorkbenchLayoutService,
} from "vscode/services";

import { useEditorRouter } from "hooks/useEditorRouter";
import { useVSCodeExtensionInitializedEffect } from "hooks/useVSCodeExtensionInitializedEffect";
import { executeVSCodeCommand } from "utils/vscode";

import { DevtoolWindowManager } from "./devtool-window-manager";
import { useEffectOpenPreview } from "./useEffectOpenPreview";

export const useLayoutSync = () => {
  const editorChannel = useEditorChannel();
  const router = useEditorRouter();
  const pitcher = usePitcher();
  const editor = useEditor();

  const [devtoolWindowManager, setDevtoolWindowManager] =
    useState<DevtoolWindowManager | null>(null);

  useLayoutButtons();
  useEffectOpenPreview();

  useEffect(() => {
    if (!pitcher.clients.setup && !devtoolWindowManager?.devtoolWindowVisible) {
      return;
    }

    const setupRestartListener = pitcher.clients.setup.onSetupProgressUpdate(
      (setupUpdate) => {
        if (setupUpdate.state === "IN_PROGRESS") {
          executeVSCodeCommand("codesandbox.openCsbTab", {
            type: "SETUP_TASKS",
          });
        }
      },
    );

    return () => {
      setupRestartListener.dispose();
    };
  }, [pitcher.clients.setup, devtoolWindowManager]);

  useEffect(() => {
    const dispose = pitcher.clients.container.onOpenSetupDevTool(
      ({ dependencies }) => {
        executeVSCodeCommand("codesandbox.openCsbTab", {
          type: "ENV_SETUP",
          dependencies,
        });
      },
    );

    return () => {
      dispose?.dispose();
    };
  }, []);

  useEffect(
    () =>
      editorChannel?.onMessage(({ data: { filepath } }) => {
        executeVSCodeCommand("codesandbox.openCsbTab", {
          type: "FILE",
          filepath: filepath,
        });
      }).dispose,
    [],
  );

  useVSCodeExtensionInitializedEffect(() => {
    const isBranchEditor = editor.type === "branch";
    const isCloudEditor = editor.type === "sandbox" && editor.isCloud;
    const enabled =
      (isBranchEditor && editor.allowedCapabilities.branch) ||
      (isCloudEditor && editor.allowedCapabilities.sandbox);

    if (router.branch.queryParams.import.get() && enabled) {
      executeVSCodeCommand("codesandbox.openCsbTab", {
        type: "ENV_SETUP",
      });

      // we don't clean the URL here because we want to keep it
      // until opening the devtool
    }
  }, []);

  useVSCodeExtensionInitializedEffect(() => {
    const devtoolWindow = new DevtoolWindowManager(pitcher);
    setDevtoolWindowManager(devtoolWindow);
    const disposable = pitcher.onInstanceChanged(({ instanceId }) => {
      devtoolWindow.setInstanceId(instanceId);
    });

    return () => {
      devtoolWindowManager?.dispose();
      disposable.dispose();
    };
  }, []);

  useVSCodeExtensionInitializedEffect(() => {
    if (!devtoolWindowManager) {
      return;
    }

    const disposeStore = new DisposableStore();

    disposeStore.add(
      vscode.commands.registerCommand(
        "codesandbox.terminal.open",
        async (taskId: string) => {
          const vscodeTask = vscode.tasks.taskExecutions.find(
            (t) => t.task.definition.taskId === taskId,
          )?.task;

          if (vscodeTask) {
            vscode.tasks.executeTask(vscodeTask);
          }
        },
      ),
    );

    disposeStore.add(
      vscode.commands.registerCommand(
        "codesandbox.openCsbTab",
        async (tab: TabParams, options?: IEditorOptions) => {
          devtoolWindowManager?.openTabFromAction(tab, options);
        },
      ),
    );

    disposeStore.add(
      vscode.commands.registerCommand("codesandbox.layout.openDevtool", () => {
        devtoolWindowManager?.openNewDevtoolTab();
      }),
    );

    disposeStore.add(
      vscode.commands.registerCommand(
        "codesandbox.layout.toggleDevtools",
        () => {
          if (devtoolWindowManager.devtoolWindowVisible) {
            devtoolWindowManager?.hideDevtoolWindow();
          } else {
            devtoolWindowManager?.showDevtoolWindow();
          }
        },
      ),
    );

    disposeStore.add(
      vscode.commands.registerCommand(
        "codesandbox.layout.closeDevtool",
        (payload: TabParams) => {
          const editorService = StandaloneServices.get(IEditorService);
          const currentEditor = editorService.editors.find((item) => {
            try {
              const uri = parseTabURI(item.resource);

              switch (payload.type) {
                case "TASK_PORT":
                case "UNASSIGNED_PORT": {
                  return (
                    uri?.type === payload.type && uri?.port === payload.port
                  );
                }

                default: {
                  return uri?.type === payload.type;
                }
              }
            } catch (err) {
              return false;
            }
          });

          currentEditor?.dispose();
        },
      ),
    );

    return () => {
      disposeStore.dispose();
    };
  }, [devtoolWindowManager]);

  /**
   * Hide terminal when editor is a browser sandbox
   */
  useVSCodeExtensionInitializedEffect(() => {
    const isBrowserSandbox = pitcher.type === "browser";

    if (isBrowserSandbox) {
      const layoutService = StandaloneServices.get(IWorkbenchLayoutService);

      layoutService.setPartHidden(
        true,
        "workbench.parts.panel" as Parts.PANEL_PART,
      );
    }
  }, []);
};
